import { z } from 'zod'

/**
 * A measurement on the wire: up to `integerDigits` digits, then at most two
 * decimal places. Two places, whatever the column's scale, because a converter
 * may send `toFixed(2)` (`"18.00"`) and a phone may have saved the two places
 * its field allows. Trailing zeros stay. An empty string, a sign, a trailing
 * dot, or a third decimal place is refused.
 *
 * Not `QUANTITY_PATTERN`. That one allows only four digits before the dot,
 * which is the wrong width for these columns.
 */
function decimalString(integerDigits: number) {
  return z
    .string()
    .regex(new RegExp(`^\\d{1,${String(integerDigits)}}(\\.\\d{1,2})?$`))
}

/**
 * Gallons a minute, `Decimal(6, 1)`: five digits, then a fraction.
 *
 * The column keeps one decimal place and `toString()` never emits more, but
 * the survey's field allows two and a phone stores what was typed. `"14.50"`
 * still reads.
 */
export const gallonsPerMinuteSchema = decimalString(5)

/** A well pump's horsepower, `Decimal(5, 2)`: three digits, then a fraction. */
export const horsepowerSchema = decimalString(3)

/**
 * A quantity, `Decimal(8, 2)`: six digits, then a fraction. `toString()` drops
 * trailing zeros, so `"2"` and `"1.5"` are what the column sends.
 */
export const measuredQuantitySchema = decimalString(6)

/** Trunk diameter in inches, `Decimal(6, 2)`. The tree converter sends `toFixed(2)`. */
export const trunkDiameterSchema = decimalString(4)

/** Length in feet, `Decimal(8, 2)`. The solution converter sends `toFixed(2)`. */
export const linearFeetSchema = decimalString(6)

/**
 * Acres, computed from a boundary with `toFixed(2)`, never stored. The width
 * is a ceiling, the same as a `Decimal(8, 2)`: nothing here is a million acres.
 */
export const acreageSchema = decimalString(6)
